import axios from 'axios';
//import { useNavigate } from "react-router-dom";
import { getAuthHeaders } from './header';
import { config } from '../config/config';

const serverUrl = `${config.BACKEND_SERVER_URL}`;

// Add these interfaces at the top of the file
interface UserData {
  email?: string;
  password?: string;
  phone?: string;
  mfaMethod?: string;
}

interface ApiResponse<T = unknown> {
  data?: T;
  error?: string | Record<string, unknown>;
  status: number;
}

interface SetLoadingFunction {
  (value: boolean): void;
}

interface LoginCredentials {
  username?: string;
  email?: string;
  password: string;
}

interface LoginResponse {
  access?: string;
  refresh?: string;
  mfa_required?: boolean;
  email?: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  message?: string;
  error?: string;
  detail?: string;
  user?: {
    email: string;
    username: string;
    first_name: string;
    last_name: string;
    role: string;
  };
}

interface MFAUpdateRequest {
  mfa_methods: string[];
}

interface ResetPasswordData {
  uidb64: string;
  token: string;
  new_password: string;
}

export const registerUser = async (
  userData: UserData,
  setLoading: SetLoadingFunction
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(`${serverUrl}/auth/signup/`, userData);
    setLoading(false);
    return { data: response.data, status: response.status }; // Return the successful response
  } catch (error: unknown) {
    setLoading(false);

    if (axios.isAxiosError(error)) {
      return {
        error: error.response?.data,
        status: error.response?.status || 500,
      };
    }

    return {
      error: 'An unexpected error occurred',
      status: 500,
    };
  }
};

export const resendVerificationLink = async (
  userData: UserData,
  setLoading: SetLoadingFunction
): Promise<ApiResponse> => {
  try {
    setLoading(true);
    const response = await axios.post(
      `${serverUrl}/auth/resend-activation-email/`,
      userData
    );
    setLoading(false);

    return { data: response.data, status: response.status }; // Return the successful response
  } catch (error: unknown) {
    setLoading(false);

    if (axios.isAxiosError(error)) {
      return {
        error: error.response?.data,
        status: error.response?.status || 500,
      };
    }

    return {
      error: 'An unexpected error occurred',
      status: 500,
    };
  }
};

export const loginUser = async (
  credentials: LoginCredentials,
  setLoading: (loading: boolean) => void
): Promise<LoginResponse> => {
  try {
    //console.log('Login attempt with credentials:', {
    //  ...credentials,
    //  password: '[REDACTED]'
    //});

    // Make sure we're sending the right field names
    const loginData = {
      username: credentials.username || credentials.email, // Backend expects 'username'
      password: credentials.password,
    };

    //console.log('Sending login data:', {
    //  ...loginData,
    //  password: '[REDACTED]',
    //});

    const response = await axios.post<LoginResponse>(
      `${config.BACKEND_SERVER_URL}/auth/login/`,
      loginData
    );

    //console.log('Login response:', response.data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('Login error response:', {
        status: error.response?.status,
        data: error.response?.data,
      });
      return error.response?.data;
    }
    console.error('Unexpected login error:', error);
    throw error;
  } finally {
    setLoading(false);
  }
};

export const verifyMfa = async (userData: UserData): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${serverUrl}/auth/verify-mfa/
`,
      userData
    );
    return { data: response.data, status: response.status };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      return {
        error: error.response?.data,
        status: error.response?.status || 500,
      };
    }

    return {
      error: 'An unexpected error occurred',
      status: 500,
    };
  }
};
export const updateMfa = async (data: MFAUpdateRequest) => {
  try {
    const response = await axios.post(
      `${config.BACKEND_SERVER_URL}/auth/mfa-settings/`,
      data,
      getAuthHeaders()
    );
    return { data: response.data, status: response.status };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        error: error.response?.data?.error || 'An error occurred',
        status: error.response?.status || 500,
      };
    }
    return {
      error: 'An unexpected error occurred',
      status: 500,
    };
  }
};

export const authGuard = async (
  setLoading: SetLoadingFunction
): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${serverUrl}/auth/auth-guard/

`,
      getAuthHeaders()
    );
    setLoading(false);

    return { data: response.data, status: response.status };
  } catch (error: unknown) {
    setLoading(false);

    if (axios.isAxiosError(error)) {
      return {
        error: error.response?.data,
        status: error.response?.status || 500,
      };
    }

    return {
      error: 'An unexpected error occurred',
      status: 500,
    };
  }
};

export const requestPasswordReset = async (
  email: string,
  setLoading: (loading: boolean) => void
): Promise<ApiResponse> => {
  try {
    setLoading(true);
    const response = await axios.post(
      `${config.BACKEND_SERVER_URL}/auth/forgot-password/`,
      { email },
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        withCredentials: true,
      }
    );
    return { data: response.data, status: response.status };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('Password reset request error:', error.response?.data);
      return {
        error:
          error.response?.data?.error || 'Failed to request password reset',
        status: error.response?.status || 500,
      };
    }
    return {
      error: 'An unexpected error occurred',
      status: 500,
    };
  } finally {
    setLoading(false);
  }
};

export const changePassword = async (
  data: ResetPasswordData,
  setLoading: (loading: boolean) => void
): Promise<ApiResponse> => {
  try {
    setLoading(true);
    const response = await axios.post(
      `${config.BACKEND_SERVER_URL}/auth/reset-password/`,
      data,
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        withCredentials: true,
      }
    );
    return { data: response.data, status: response.status };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('Password reset error:', error.response?.data);
      return {
        error: error.response?.data?.error || 'Failed to reset password',
        status: error.response?.status || 500,
      };
    }
    return {
      error: 'An unexpected error occurred',
      status: 500,
    };
  } finally {
    setLoading(false);
  }
};
